<div class="container py-4 bill-pay">
  <h2 class="page-title mb-4">Billing Details</h2>

  @if (loading) {
    <div class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  }

  @if (errorMessage) {
    <div class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>
  }

  @if (successMessage) {
    <div class="alert alert-success" role="alert">
      <h5 class="alert-heading">Payment Successful</h5>
      <p>{{ successMessage }}</p>
      @if (paymentResult) {
        <hr />
        <p class="mb-0">
          <strong>Transaction ID:</strong> {{ paymentResult.transactionId }}<br />
          <strong>Amount Charged:</strong> {{ paymentResult.amountCharged | currency }}
        </p>
      }
    </div>
  }

  @if (details && !successMessage) {
    <!-- Customer & Balance Summary -->
    <div class="card summary-card mb-4">
      <div class="card-body">
        <div class="row align-items-center gy-3">
          <div class="col-md-8">
            <h4 class="card-title mb-1">{{ details.businessName }}</h4>
            <div class="text-muted">{{ details.domain }}</div>
          </div>
          <div class="col-md-4 text-md-end">
            <div class="small text-uppercase text-muted fw-semibold">Total Amount Due</div>
            <div class="amount-due">{{ details.totalAmountDue | currency }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Billable Items Table -->
    @if (details.billableItems && details.billableItems.length > 0) {
      <div class="table-responsive mb-4">
        <table class="table table-striped">
          <thead>
            <tr>
              <th>Product</th>
              <th>Domain</th>
              <th class="text-end">Renewal Price</th>
              <th>Renewal Date</th>
              <th>Frequency (months)</th>
            </tr>
          </thead>
          <tbody>
            @for (item of details.billableItems; track item.id) {
              <tr>
                <td>{{ item.productDescription }}</td>
                <td>{{ item.domain }}</td>
                <td class="text-end">{{ item.renewalPrice | currency }}</td>
                <td>{{ item.renewalDate | date : 'MM/dd/yyyy' }}</td>
                <td>{{ item.renewalFrequency }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else {
      <p class="text-muted">No items due for billing found.</p>
    }

    <!-- Payment Form -->
    @if (details.totalAmountDue > 0) {
      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">Payment Information</h5>
        </div>
        <div class="card-body">
          <form [formGroup]="paymentForm" (ngSubmit)="onSubmit()">
            <!-- Billing Address Section -->
            <h6 class="mb-3">Billing Address</h6>
            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="ccFirstName" class="form-label">First Name</label>
                <input
                  type="text"
                  id="ccFirstName"
                  class="form-control"
                  formControlName="ccFirstName"
                  [class.is-invalid]="isInvalid('ccFirstName')"
                />
                @if (isInvalid('ccFirstName')) {
                  <div class="invalid-feedback">First name is required.</div>
                }
              </div>
              <div class="col-md-6 mb-3">
                <label for="ccLastName" class="form-label">Last Name</label>
                <input
                  type="text"
                  id="ccLastName"
                  class="form-control"
                  formControlName="ccLastName"
                  [class.is-invalid]="isInvalid('ccLastName')"
                />
                @if (isInvalid('ccLastName')) {
                  <div class="invalid-feedback">Last name is required.</div>
                }
              </div>
            </div>

            <div class="mb-3">
              <label for="ccAddress1" class="form-label">Address Line 1</label>
              <input
                type="text"
                id="ccAddress1"
                class="form-control"
                formControlName="ccAddress1"
                [class.is-invalid]="isInvalid('ccAddress1')"
              />
              @if (isInvalid('ccAddress1')) {
                <div class="invalid-feedback">Address is required.</div>
              }
            </div>

            <div class="mb-3">
              <label for="ccAddress2" class="form-label">Address Line 2</label>
              <input type="text" id="ccAddress2" class="form-control" formControlName="ccAddress2" />
            </div>

            <div class="row">
              <div class="col-md-5 mb-3">
                <label for="ccCity" class="form-label">City</label>
                <input
                  type="text"
                  id="ccCity"
                  class="form-control"
                  formControlName="ccCity"
                  [class.is-invalid]="isInvalid('ccCity')"
                />
                @if (isInvalid('ccCity')) {
                  <div class="invalid-feedback">City is required.</div>
                }
              </div>
              <div class="col-md-3 mb-3">
                <label for="ccState" class="form-label">State</label>
                <select
                  id="ccState"
                  class="form-select"
                  formControlName="ccState"
                  [class.is-invalid]="isInvalid('ccState')"
                >
                  <option value="">Select...</option>
                  @for (state of states; track state) {
                    <option [value]="state">{{ state }}</option>
                  }
                </select>
                @if (isInvalid('ccState')) {
                  <div class="invalid-feedback">State is required.</div>
                }
              </div>
              <div class="col-md-4 mb-3">
                <label for="ccZip" class="form-label">Zip Code</label>
                <input
                  type="text"
                  id="ccZip"
                  class="form-control"
                  formControlName="ccZip"
                  [class.is-invalid]="isInvalid('ccZip')"
                />
                @if (isInvalid('ccZip')) {
                  <div class="invalid-feedback">Zip code is required.</div>
                }
              </div>
            </div>

            <hr class="my-4" />

            <!-- Credit Card Section -->
            <h6 class="mb-3">Credit Card Details</h6>
            <div class="row align-items-end">
              <div class="col-12 col-md mb-3">
                <label for="ccNumber" class="form-label">Card Number</label>
                <input
                  type="text"
                  id="ccNumber"
                  class="form-control"
                  formControlName="ccNumber"
                  [mask]="ccNumberMask"
                  [validation]="false"
                  [showMaskTyped]="false"
                  placeholder="____-____-____-____"
                  autocomplete="off"
                  [class.is-invalid]="isInvalid('ccNumber')"
                />
                @if (isInvalid('ccNumber')) {
                  @if (paymentForm.get('ccNumber')?.errors?.['invalidAmex']) {
                    <div class="invalid-feedback">A 15-digit card number must be an American Express card.</div>
                  } @else {
                    <div class="invalid-feedback">A valid card number is required.</div>
                  }
                }
              </div>
              <div class="col-5 col-md-2 mb-3">
                <label for="ccExp" class="form-label">Exp</label>
                <input
                  type="text"
                  id="ccExp"
                  class="form-control"
                  formControlName="ccExp"
                  [mask]="'M0/0000'"
                  [showMaskTyped]="true"
                  [dropSpecialCharacters]="false"
                  autocomplete="off"
                  placeholder="MM/YYYY"
                  [class.is-invalid]="isInvalid('ccExp')"
                />
                @if (isInvalid('ccExp')) {
                  <div class="invalid-feedback">Required.</div>
                }
              </div>
              <div class="col-3 col-md-1 mb-3">
                <label for="cvv" class="form-label">CVV</label>
                <input
                  type="text"
                  id="cvv"
                  class="form-control"
                  formControlName="cvv"
                  maxlength="4"
                  autocomplete="off"
                  [class.is-invalid]="isInvalid('cvv')"
                />
                @if (isInvalid('cvv')) {
                  <div class="invalid-feedback">CVV is required.</div>
                }
              </div>
              <div class="col-4 col-md-2 mb-3 d-flex align-items-end">
                @if (cardInfo) {
                  <img
                    style="height: 37.5px"
                    [src]="cardInfo.src"
                    [title]="cardInfo.tooltip"
                    [alt]="cardInfo.alt"
                    class="mb-2"
                  />
                }
              </div>
            </div>

            <hr class="my-4" />

            <p class="text-muted small mb-3">
              The credit card information you enter will be securely stored for recurring payments. If you have any questions, please call customer service at {{ brandService.brand.phone }}.
            </p>

            <div class="d-flex justify-content-between align-items-center">
              <span class="fs-5">
                <strong>Amount to be charged: {{ details.totalAmountDue | currency }}</strong>
              </span>
              <button type="submit" class="btn btn-portal-accent btn-lg" [disabled]="submitting">
                @if (submitting) {
                  <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                  Processing...
                } @else {
                  Submit Payment
                }
              </button>
            </div>
          </form>
        </div>
      </div>
    }
  }
</div>
